import { useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { AZURE_AD_CONFIG } from '@/config';
import { HugeiconsIcon } from '@hugeicons/react';
import { Loading02Icon } from '@hugeicons/core-free-icons';

export default function AuthCallback() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const processed = useRef(false);

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;

    const handleCallback = async () => {
      try {
        const code = searchParams.get('code');
        const state = searchParams.get('state');

        if (!code) {
          toast.error('No se recibió el código de autorización');
          navigate('/login', { replace: true });
          return;
        }

        // Validate state matches stored value
        const storedState = sessionStorage.getItem('via_pkce_state');
        if (!state || state !== storedState) {
          toast.error('Error de seguridad: el estado de la solicitud no coincide');
          navigate('/login', { replace: true });
          return;
        }

        // Get PKCE code_verifier
        const codeVerifier = sessionStorage.getItem('via_pkce_code_verifier');
        if (!codeVerifier) {
          toast.error('Error: no se encontró el verificador PKCE');
          navigate('/login', { replace: true });
          return;
        }

        // Exchange code for tokens directly with Azure AD from the browser (SPA PKCE flow).
        // This avoids the backend needing internet access from within the VPC.
        const { tenantId, clientId, redirectUri } = AZURE_AD_CONFIG;
        const tokenUrl = `https://login.microsoftonline.com/${tenantId}/oauth2/v2.0/token`;
        const tokenBody = new URLSearchParams({
          grant_type: 'authorization_code',
          client_id: clientId,
          code,
          code_verifier: codeVerifier,
          redirect_uri: redirectUri,
        });

        const tokenResp = await fetch(tokenUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          body: tokenBody,
        });
        const tokenData = await tokenResp.json();

        if (tokenData.error) {
          toast.error(tokenData.error_description || 'Error al obtener token de Microsoft');
          navigate('/login', { replace: true });
          return;
        }

        if (!tokenData.id_token) {
          toast.error('No se recibió el id_token de Microsoft');
          navigate('/login', { replace: true });
          return;
        }

        // Send the id_token to backend for validation and user provisioning
        const { data } = await api.post('/auth/azure/callback', {
          id_token: tokenData.id_token,
        });

        // Store JWT and user in sessionStorage
        sessionStorage.setItem('via_token', data.token);
        sessionStorage.setItem('via_user', JSON.stringify(data.user));

        // Navigate to home — use full page reload to ensure AuthProvider reads new session.
        window.location.href = '/';
      } catch (err) {
        const message =
          err?.response?.data?.detail ||
          err?.response?.data?.error ||
          err?.message ||
          'Error al autenticar con Microsoft';
        toast.error(message);
        navigate('/login', { replace: true });
      } finally {
        // Clean up PKCE values
        sessionStorage.removeItem('via_pkce_state');
        sessionStorage.removeItem('via_pkce_code_verifier');
      }
    };

    handleCallback();
  }, [searchParams, navigate]);

  return (
    <div className="flex min-h-screen items-center justify-center">
      <div className="flex flex-col items-center gap-4">
        <HugeiconsIcon icon={Loading02Icon} className="h-8 w-8 animate-spin text-primary" aria-label="Cargando" />
        <p className="text-sm text-muted-foreground">Autenticando con Microsoft...</p>
      </div>
    </div>
  );
}
